/**
 * lx-chat-view —— 会话详情（覆盖层）
 *
 * 展示与某联系人的完整对话，底部带发送框 + 附件菜单。
 * 消息气泡按发送者分左右：'me' 在右（主色底），对方在左（表面色底）。
 *
 * 这里的发送是真发送：写进 IndexedDB，会出现在会话列表的预览里。
 * 对方不会自动回复 —— 无 mock 原则（D5）明确不模拟「对方回消息」。
 * 用户如果想看到对面说话，用 AI 以对方身份发（`session.sendFromContact`）或
 * 直接在数据层构造 —— 但那是用户主动的，不是应用自演的。
 *
 * 附件（M5 补）：底部加号菜单提供三条真实入口 ——
 *   浏览照片 / 浏览文件 / 直接拍照。三者最终都走
 *   `filesRepo.attachToMessage()` 写 messages + files 两张表，
 *   与 AI 路径（`file.sendPhoto`）共用同一个实现。
 */

import { LxElement, define } from '@/core/base-element';
import { sessionsRepo } from '@/data/repo.sessions';
import { messagesRepo } from '@/data/repo.messages';
import { contactsRepo } from '@/data/repo.contacts';
import { filesRepo, formatSize } from '@/data/repo.files';
import { popOverlay, pushOverlay } from '@/core/ui-store';
import { setAttachTarget } from '@/core/attach-target';
import { trace } from '@/ai/trace';
import type { Message, Contact, MessageType, StoredFile } from '@/types/models';

export class LxChatView extends LxElement {
  private _sessionId = '';
  private _contact: Contact | null = null;
  private _messages: Message[] = [];
  private _sending = false;
  /** 附件菜单是否展开。展开时面板从底部推起（见 .composer-wrap 的样式） */
  private _menuOpen = false;
  /** 本会话的附件记录，按 fileId 索引 —— 气泡渲染时查缩略图/文件名 */
  private _files = new Map<string, StoredFile>();
  /** 本轮渲染创建、已交给 DOM 的 object URL */
  private _objectUrls: string[] = [];
  /** 上一轮遗留的 object URL —— 本轮新 URL 建好后统一回收 */
  private _staleUrls: string[] = [];

  set sessionId(value: string) {
    this._sessionId = value;
    void this.load();
  }

  get sessionId(): string {
    return this._sessionId;
  }

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-overlay);
        display: flex;
        flex-direction: column;
        background: var(--c-bg);
        animation: slide-in var(--dur-base) var(--ease-standard);
      }

      @keyframes slide-in {
        from { transform: translateX(6%); opacity: .4; }
        to { transform: translateX(0); opacity: 1; }
      }

      header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--sp-3);
        padding: var(--sp-3) var(--layout-content-pad-x);
        padding-top: calc(var(--sp-3) + env(safe-area-inset-top, 0px));
        background: var(--c-surface);
        border-bottom: 1px solid var(--c-border);
      }

      .back {
        width: 34px;
        height: 34px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        color: var(--c-fg);
        flex-shrink: 0;
      }

      .back:hover { background: var(--c-surface-2); }
      .back svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; }

      .title {
        flex: 1;
        min-width: 0;
        font-size: var(--fs-lg);
        font-weight: var(--fw-medium);
        color: var(--c-fg);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .thread {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        display: flex;
        flex-direction: column;
        gap: var(--sp-3);
        padding: var(--sp-4) var(--layout-content-pad-x);
      }

      .empty {
        margin: auto;
        text-align: center;
        color: var(--c-fg-subtle);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
      }

      .bubble-row {
        display: flex;
        justify-content: flex-start;
      }

      .bubble-row.mine { justify-content: flex-end; }

      /* 气泡宽度靠内容撑，不靠 max-width 撑。
         ⚠️ 这里**不能**在 .bubble 上写 white-space: pre-wrap ——
         见下方 .bubble .text 的说明。 */
      .bubble {
        max-width: 76%;
        padding: var(--sp-2) var(--sp-3);
        border-radius: var(--radius-md);
        background: var(--c-surface);
        border: 1px solid var(--c-border);
        color: var(--c-fg);
        font-size: var(--fs-md);
        line-height: var(--lh-base);
        word-break: break-word;
      }

      /* 多行文本的换行保留放在内层，而不是放在 .bubble 上。
         原因：render() 返回的是带缩进的模板字符串，若 .bubble 是 pre-wrap，
         那段缩进（换行 + 前导空格）会被**当成真实空白渲染出来** ——
         气泡因此凭空变宽、上下各多出一个空行。
         把 pre-wrap 收进内层 span，缩进落在 span **外面**，不参与渲染。 */
      .bubble .text {
        white-space: pre-wrap;
      }

      .bubble-row.mine .bubble {
        background: var(--c-primary);
        border-color: transparent;
        color: var(--c-primary-fg);
      }

      /* 附件气泡：图片直接用自身留白，文件/语音给一张小卡片。 */
      .bubble.media {
        padding: 4px;
        overflow: hidden;
      }

      .bubble.media img {
        display: block;
        max-width: 200px;
        max-height: 260px;
        border-radius: calc(var(--radius-md) - 3px);
        object-fit: cover;
      }

      .attach-card {
        display: flex;
        align-items: center;
        gap: var(--sp-2);
        min-width: 160px;
        padding: var(--sp-1) var(--sp-2) var(--sp-1) var(--sp-1);
      }

      .attach-card .ic {
        flex-shrink: 0;
        width: 34px;
        height: 34px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-sm);
        background: color-mix(in srgb, currentColor 14%, transparent);
      }

      .attach-card .ic svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      /* 文件名与体积上下两行 —— span 默认是行内，不设 flex 会挤在同一行 */
      .attach-card .meta {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .attach-card .fn {
        font-size: var(--fs-sm);
        line-height: 1.25;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .attach-card .fs {
        font-size: 10px;
        line-height: 1;
        opacity: .7;
      }

      /* 附件没取到（blob 丢失）时的兜底，不装成正常的 */
      .attach-missing {
        font-size: var(--fs-sm);
        opacity: .7;
      }

      .stamp {
        margin-top: 2px;
        font-size: 10px;
        /* 行高压到 1：时间戳不该再按正文字号的行高撑高度 */
        line-height: 1;
        /* 靠右贴边，不占一整行宽度 */
        text-align: right;
        /* 用「继承色 + 透明度」而不是固定灰：
           气泡底色可能是主色（亮黄绿），固定灰会在上面糊成一团。
           继承文字色再降透明度，在深色底和主色底上都成立。 */
        opacity: .55;
      }

      /* 附件卡片里的时间戳也靠右，与文本气泡一致 */
      .bubble.media .stamp {
        margin-right: 2px;
      }

      /* 底部区域 = 附件面板 + 输入行，整体贴底。
         面板展开时它变高，把上方的 .thread 挤上去 —— 这就是「推起」效果：
         不需要绝对定位遮盖，靠 flex 自然分配即可。 */
      .composer-wrap {
        flex-shrink: 0;
        background: var(--c-surface);
        border-top: 1px solid var(--c-border);
        padding-bottom: env(safe-area-inset-bottom, 0px);
      }

      .attach-menu {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--sp-2);
        padding: var(--sp-3) var(--layout-content-pad-x);
        border-bottom: 1px solid var(--c-border);
        animation: rise var(--dur-base) var(--ease-standard);
      }

      @keyframes rise {
        from { transform: translateY(14px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .attach-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: var(--sp-3) var(--sp-1);
        border-radius: var(--radius-md);
        background: var(--c-surface-2);
        border: 1px solid var(--c-border);
        color: var(--c-fg);
        font-size: var(--fs-sm);
        transition: background var(--dur-fast) var(--ease-standard);
      }

      .attach-item:hover { background: var(--c-border); }
      .attach-item:active { transform: scale(.97); }

      .attach-item svg {
        width: 22px;
        height: 22px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .composer {
        display: flex;
        align-items: flex-end;
        gap: var(--sp-2);
        padding: var(--sp-2) var(--layout-content-pad-x);
      }

      /* 加号按钮：与发送按钮同尺寸，横向并排 */
      .plus {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        background: var(--c-surface-2);
        border: 1px solid var(--c-border);
        color: var(--c-fg);
        transition: background var(--dur-fast) var(--ease-standard),
                    transform var(--dur-fast) var(--ease-standard);
      }

      .plus:hover { background: var(--c-border); }
      .plus svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }

      /* 展开时加号转 45° 变叉 —— 一个按钮兼作开与关，省一个位置 */
      .plus.open svg { transform: rotate(45deg); }
      .plus svg { transition: transform var(--dur-base) var(--ease-standard); }

      textarea {
        flex: 1;
        min-height: 40px;
        max-height: 96px;
        padding: var(--sp-2) var(--sp-3);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-lg);
        background: var(--c-surface-2);
        color: var(--c-fg);
        font-size: var(--fs-md);
        line-height: var(--lh-base);
        resize: none;
        overflow-y: auto;
      }

      textarea:focus { outline: none; border-color: var(--c-accent); }
      textarea::placeholder { color: var(--c-fg-subtle); }

      .send {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        background: var(--c-primary);
        color: var(--c-primary-fg);
        transition: opacity var(--dur-fast) var(--ease-standard);
      }

      .send:disabled { opacity: .4; cursor: not-allowed; }
      .send svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; }
    `;
  }

  protected render(): string {
    const name = this._contact?.remark || this._contact?.name || '会话';

    return `
      <header>
        <button class="back" type="button" data-back aria-label="返回">
          <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2 class="title">${this.esc(name)}</h2>
      </header>

      <div class="thread" data-thread>
        ${
          this._messages.length === 0
            ? `<p class="empty">还没有消息。<br>在下面输入，或者让 AI 替你发。</p>`
            : this._messages.map((m) => this.renderBubble(m)).join('')
        }
      </div>

      <div class="composer-wrap">
        ${this._menuOpen ? this.renderAttachMenu() : ''}
        <div class="composer">
          <button class="plus ${this._menuOpen ? 'open' : ''}" type="button" data-plus
                  aria-label="添加附件" aria-expanded="${this._menuOpen}">
            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
          </button>
          <textarea rows="1" placeholder="发消息…" aria-label="输入消息"></textarea>
          <button class="send" type="button" disabled aria-label="发送">
            <svg viewBox="0 0 24 24"><path d="M4.5 12h14M12.5 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>
    `;
  }

  /** 附件菜单：三条真实入口。拍照走覆盖层，另两条走隐藏的 <input type="file"> */
  private renderAttachMenu(): string {
    return `
      <div class="attach-menu" role="menu">
        <button class="attach-item" type="button" data-attach="photo" role="menuitem">
          <svg viewBox="0 0 24 24">
            <rect x="3" y="5" width="18" height="14" rx="2"/>
            <circle cx="8.5" cy="10.5" r="1.6"/>
            <path d="M4 17l4.5-4.5 3 3L15 12l5 5"/>
          </svg>
          照片
        </button>
        <button class="attach-item" type="button" data-attach="file" role="menuitem">
          <svg viewBox="0 0 24 24">
            <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z"/>
            <path d="M13 3v6h6"/>
          </svg>
          文件
        </button>
        <button class="attach-item" type="button" data-attach="camera" role="menuitem">
          <svg viewBox="0 0 24 24">
            <path d="M4 8h2.5L8 5.5h8L17.5 8H20a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
            <circle cx="12" cy="13" r="3.2"/>
          </svg>
          拍照
        </button>
      </div>
    `;
  }

  private renderBubble(m: Message): string {
    const mine = m.sender === 'me';
    const body = this.renderBubbleBody(m);
    // ⚠️ 这里刻意**不缩进** —— 外层行内不出多余空白，
    //    免得任何一处 white-space 变化又把缩进渲染成可见空白（见 .bubble 的注释）。
    return `<div class="bubble-row ${mine ? 'mine' : ''}"><div class="bubble ${m.type === 'image' ? 'media' : ''}">${body}<div class="stamp">${formatStamp(m.createdAt)}</div></div></div>`;
  }

  /**
   * 气泡内容：文本消息直接出字，附件消息查 files 表渲染。
   *
   * files 记录不一定能取到（比如 blob 写入曾失败），
   * 那种情况下**不假装有图** —— 给一个明确的缺附件提示，
   * 而不是留一块空白让用户以为加载慢。
   */
  private renderBubbleBody(m: Message): string {
    if (m.type === 'text' || !m.fileId) {
      // 换行保留靠这层 span（不是 .bubble，理由见样式注释）
      return `<span class="text">${this.esc(m.text)}</span>`;
    }

    const f = this._files.get(m.fileId);
    if (!f) {
      return `<span class="attach-missing">[附件] ${this.esc(m.text)}</span>`;
    }

    if (m.type === 'image') {
      const url = this.objectUrl(f.blob);
      return `<img src="${url}" alt="${this.esc(f.name)}" loading="lazy">`;
    }

    const icon = m.type === 'voice' ? ICON_VOICE : ICON_FILE;
    return `<div class="attach-card"><span class="ic">${icon}</span><span class="meta"><span class="fn">${this.esc(f.name)}</span><span class="fs">${formatSize(f.size)}</span></span></div>`;
  }

  /** blob → object URL，登记起来以便下一轮回收 */
  private objectUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this._objectUrls.push(url);
    return url;
  }

  /**
   * 回收上一轮的 object URL。
   *
   * 时机很关键：不能在 render() 之前就 revoke —— 那一刻 DOM 里还挂着
   * 上一轮的 <img>，URL 一断图就白了。所以是「新一轮的 URL 全部建好、
   * 新 DOM 已经写进去」之后（也就是 onMounted 里）再把上一轮的那批放掉：
   * 此时旧 <img> 已被 innerHTML 覆写，引用计数归零，安全。
   */
  private revokeStaleUrls(): void {
    for (const url of this._staleUrls) URL.revokeObjectURL(url);
    this._staleUrls = this._objectUrls;
    this._objectUrls = [];
  }

  protected onMounted(): void {
    // 新 DOM 已写入，此时放掉上一轮的 blob URL 才安全
    this.revokeStaleUrls();

    this.$('[data-back]')?.addEventListener('click', () => popOverlay());

    const ta = this.$('textarea') as HTMLTextAreaElement | null;
    const sendBtn = this.$('.send') as HTMLButtonElement | null;
    if (!ta || !sendBtn) return;

    const autoGrow = (): void => {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`;
      sendBtn.disabled = ta.value.trim().length === 0 || this._sending;
    };

    ta.addEventListener('input', autoGrow);
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        void this.send();
      }
    });
    sendBtn.addEventListener('click', () => void this.send());

    /* ---------- 附件 ---------- */

    // 加号：开合菜单。
    // ⚠️ 展开/收起会调 update()，于是这里的监听在下一轮渲染后会重新绑 ——
    //    这正是我们想要的（每次渲染后 DOM 是新的，必须重绑）。
    //    不要在别处把监听挂到 window 上，那才会叠加。
    this.$('[data-plus]')?.addEventListener('click', () => {
      this._menuOpen = !this._menuOpen;
      this.update();
      // 菜单展开后让输入框保持聚焦不方便（点加号已经夺焦），
      // 但收起时把焦点还给输入框是顺手的
      if (!this._menuOpen) queueMicrotask(() => ta.focus());
    });

    this.$('[data-attach="photo"]')?.addEventListener('click', () => {
      this.pickAndSend('image/*', 'image');
    });
    this.$('[data-attach="file"]')?.addEventListener('click', () => {
      this.pickAndSend('*/*', 'file');
    });
    this.$('[data-attach="camera"]')?.addEventListener('click', () => {
      // 先记下「拍完发给谁」，再推摄像头覆盖层。
      // 覆盖层只带 {kind, refId}，带不了这个上下文 —— 所以走 attach-target 暂存。
      setAttachTarget({ sessionId: this._sessionId });
      this._menuOpen = false;
      this.update();
      pushOverlay({ kind: 'camera', refId: '' });
    });

    // 打开时滚到底部
    const thread = this.$('[data-thread]');
    if (thread) thread.scrollTop = thread.scrollHeight;
    autoGrow();
  }

  /**
   * 用一个隐藏的 <input type="file"> 选文件，选完立即发送。
   *
   * 为什么现建现用、而不是在 render 里放一个常驻 input：
   *   常驻的话每次 update() 都会重建它，用户选到一半就没了。
   *   而且同一个 input 反复用需要重置 value，容易忘 —— 建一次用一次最干净。
   */
  private pickAndSend(accept: string, kind: 'image' | 'file'): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    // 相册里可能有多张，允许一次选多个
    input.multiple = true;
    input.style.display = 'none';

    input.addEventListener('change', () => {
      const files = Array.from(input.files ?? []);
      input.remove();
      if (files.length > 0) void this.sendFiles(files, kind);
    });

    document.body.appendChild(input);
    input.click();
  }

  /**
   * 把选中的文件发出去。
   *
   * 两种类型走同一个 repo 方法（`attachToMessage`），
   * 与 AI 路径的 `file.sendPhoto` 共用实现 —— 保证「发出去的东西
   * 一定在 files 表里有对应记录」，否则 file.summarize() 会漏掉它们。
   */
  private async sendFiles(files: File[], fallbackKind: 'image' | 'file'): Promise<void> {
    if (!this._sessionId || files.length === 0) return;

    this._sending = true;
    try {
      let failed = 0;
      for (const f of files) {
        // 类型以文件自身的 mime 为准，fallback 只兜住 mime 为空的情况
        // （某些系统对无扩展名文件不给 mime）
        const mime = f.type || (fallbackKind === 'image' ? 'image/*' : 'application/octet-stream');
        const kind: MessageType = mime.startsWith('image/')
          ? 'image'
          : mime.startsWith('audio/')
            ? 'voice'
            : 'file';
        try {
          await filesRepo.attachToMessage({
            sessionId: this._sessionId,
            sender: 'me',
            name: f.name || '未命名文件',
            blob: f,
            mime,
            kind,
          });
        } catch (err) {
          failed += 1;
          trace.note(`✗ 附件发送失败：${f.name} —— ${describeError(err)}`);
        }
      }

      await sessionsRepo.markRead(this._sessionId);
      await this.load();

      if (failed > 0) {
        // 有失败就如实说，不要假装全成功（信任原则的本地版）
        trace.note(`附件发送：${files.length - failed} 成功 / ${failed} 失败`);
      } else {
        trace.note(`✓ 已发送 ${files.length} 个附件`);
      }
    } finally {
      this._sending = false;
    }
  }

  private async send(): Promise<void> {
    const ta = this.$('textarea') as HTMLTextAreaElement | null;
    if (!ta || this._sending) return;

    const text = ta.value.trim();
    if (!text || !this._sessionId) return;

    this._sending = true;
    ta.value = '';
    try {
      await messagesRepo.append({
        sessionId: this._sessionId,
        sender: 'me',
        text,
      });
      await sessionsRepo.markRead(this._sessionId);
      await this.load();
    } finally {
      this._sending = false;
    }
  }

  private async load(): Promise<void> {
    if (!this._sessionId) return;

    const session = await sessionsRepo.get(this._sessionId);
    if (!session) {
      // 会话已被删除（可能 AI 删了联系人），直接退出这层
      popOverlay();
      return;
    }

    this._contact = (await contactsRepo.get(session.contactId)) ?? null;
    this._messages = await messagesRepo.listBySession(this._sessionId);

    // 一次性把本会话的附件全捞出来建索引。
    // 消息条数通常远小于文件数上限，这里不做「只查缺的」那种优化 ——
    // 一次 dbQueryByIndex 的成本可以忽略，换来渲染时同步可查。
    const files = await filesRepo.listBySession(this._sessionId);
    this._files = new Map(files.map((f) => [f.id, f]));

    this.update();

    // 渲染后滚到底
    queueMicrotask(() => {
      const thread = this.$('[data-thread]');
      if (thread) thread.scrollTop = thread.scrollHeight;
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    this.subscribe('message:appended', (p) => {
      if (p.sessionId === this._sessionId) void this.load();
    });
    this.subscribe('message:list-changed', (p) => {
      if (p.sessionId === this._sessionId) void this.load();
    });
  }

  protected onUnmounted(): void {
    // 组件走了，最后一批 URL 也得放掉，否则整个会话的图片 blob 都留着
    for (const url of this._objectUrls) URL.revokeObjectURL(url);
    for (const url of this._staleUrls) URL.revokeObjectURL(url);
    this._objectUrls = [];
    this._staleUrls = [];
  }
}

function formatStamp(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** 错误转一句人话（仅用于控制台留痕，不用于 UI 展示） */
function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/* 非图片附件的卡片图标（stroke 走 currentColor，深浅底都成立） */
const ICON_FILE = `<svg viewBox="0 0 24 24"><path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z"/><path d="M13 3v6h6"/></svg>`;

const ICON_VOICE = `<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3"/></svg>`;

define('lx-chat-view', LxChatView);
